import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Stack,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  LinearProgress,
  Divider,
  Button,
  Menu,
  ListItemIcon,
  ListItemText,
  Checkbox,
  FormControlLabel,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  Avatar,
} from '@mui/material';
import {
  ArrowBack,
  ViewModule,
  Person,
  FilterList,
  Download,
  ExpandMore,
  ExpandLess,
  AssignmentInd,
  Flag,
  Search,
  Close,
  DateRange,
} from '@mui/icons-material';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip as ChartTooltip, Legend, ChartOptions, ChartDataset } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { useProjectStore } from '../store/useProjectStore';
import { Task, Subtask, Role, Priority, TaskStatus } from '../types';
import { format, differenceInDays, startOfDay, endOfDay, startOfMonth, endOfMonth, isWithinInterval } from 'date-fns';
import Header from '../components/Header';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { zhTW } from 'date-fns/locale';
import { styled } from '@mui/material/styles';

// 註冊 Chart.js 組件
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, ChartTooltip, Legend, ChartDataLabels);

// 定義顏色映射
const roleColors: Record<Role, string> = {
  'UI': '#ff6b6b',
  'UX': '#4ecdc4',
  '前端': '#667eea',
  '後端': '#45b7d1',
  '系統架構師': '#f7b731',
  'AI工程師': '#a55eea',
};

const priorityColors: Record<Priority, string> = {
  'high': '#e53e3e',
  'medium': '#f6ad55',
  'low': '#48bb78',
};

const taskColors: Record<string, string> = {
  '註冊功能': 'linear-gradient(135deg, #667eea, #764ba2)',
  '肌膚檢測功能': 'linear-gradient(135deg, #f093fb, #f5576c)',
  '電商功能': 'linear-gradient(135deg, #4facfe, #00f2fe)',
  '預約功能': 'linear-gradient(135deg, #43e97b, #38f9d7)',
};

const statusColors: Record<TaskStatus, string> = {
  'pending': '#e2e8f0',
  'in-progress': '#4299e1',
  'pending-review': '#f6ad55',
  'reviewing': '#9f7aea',
  'completed': '#48bb78',
  'review-failed': '#e53e3e',
};

type ViewMode = 'role' | 'person';
type FilterType = {
  tasks: string[];
  subtasks: string[];
  roles: Role[];
  assignees: string[];
  taskPriorities: Priority[];
  subtaskStatuses: string[];
  showCompleted: boolean;
  showExpected: boolean;
};

interface GanttBar {
  id: string;
  label: string;
  task: string;
  role: Role;
  assignees: string[];
  startDate: Date;
  endDate: Date;
  status: TaskStatus;
  progress: number;
}

const PageContainer = styled(Box)({
  minHeight: '100vh',
  backgroundColor: '#f5f7fa',
  paddingBottom: 48,
});

const ContentWrapper = styled(Container)(({ theme }) => ({
  paddingTop: 24,
  paddingBottom: 24,
  [theme.breakpoints.up('lg')]: {
    paddingLeft: 32,
    paddingRight: 32,
  },
}));

const StatsGrid = styled(Box)({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
  gap: 24,
  marginBottom: 32,
});

const StatCard = styled(Paper)({
  padding: 24,
  borderRadius: 12,
  backgroundColor: '#ffffff',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
  border: '1px solid #e2e8f0',
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
    transform: 'translateY(-2px)',
  },
});

const StatIcon = styled(Box)<{ color: string }>(({ color }) => ({
  width: 48,
  height: 48,
  borderRadius: 12,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: 16,
  background: color,
  '& .MuiSvgIcon-root': {
    fontSize: '1.5rem',
    color: '#ffffff',
  },
}));

const StatLabel = styled(Typography)({
  fontSize: '0.875rem',
  fontWeight: 600,
  color: '#64748b',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  marginBottom: 4,
});

const StatValue = styled(Typography)({
  fontSize: '1.75rem',
  fontWeight: 700,
  color: '#1a202c',
  lineHeight: 1.2,
});

const ChartContainer = styled(Paper)({
  padding: 24,
  borderRadius: 12,
  backgroundColor: '#ffffff',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
  border: '1px solid #e2e8f0',
  marginBottom: 32,
  overflow: 'hidden',
});

const ChartHeader = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  marginBottom: 24,
  flexWrap: 'wrap',
  gap: 16,
});

const ChartTitle = styled(Typography)({
  fontSize: '1.25rem',
  fontWeight: 700,
  color: '#1a202c',
  letterSpacing: '-0.02em',
});

const TimeDistributionSection = styled(Paper)({
  padding: 24,
  borderRadius: 12,
  backgroundColor: '#ffffff',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
  border: '1px solid #e2e8f0',
  marginTop: 32,
});

const FilterSection = styled(Box)({
  display: 'flex',
  gap: 16,
  marginBottom: 24,
  flexWrap: 'wrap',
  alignItems: 'flex-end',
  padding: 20,
  backgroundColor: '#f8fafc',
  borderRadius: 8,
  border: '1px solid #e2e8f0',
});

const TimeDistributionGrid = styled(Box)({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
  gap: 20,
  marginTop: 24,
});

const TimeCard = styled(Box)({
  backgroundColor: '#f8fafc',
  borderRadius: 12,
  padding: 20,
  border: '1px solid #e2e8f0',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: '#f1f5f9',
    borderColor: '#cbd5e1',
    transform: 'translateY(-1px)',
  },
});

const PersonAvatar = styled(Avatar)({
  width: 36,
  height: 36,
  fontSize: '0.875rem',
  fontWeight: 600,
  marginRight: 12,
});

const ProgressBar = styled(LinearProgress)({
  height: 8,
  borderRadius: 4,
  backgroundColor: '#e2e8f0',
  marginTop: 8,
  marginBottom: 8,
  '& .MuiLinearProgress-bar': {
    borderRadius: 4,
  },
});

const StyledButton = styled(Button)({
  textTransform: 'none',
  fontWeight: 500,
  borderRadius: 8,
  padding: '8px 16px',
  fontSize: '0.875rem',
  boxShadow: 'none',
  '&:hover': {
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
  },
});

const ViewToggleGroup = styled(ToggleButtonGroup)({
  backgroundColor: '#ffffff',
  border: '1px solid #e2e8f0',
  borderRadius: 8,
  '& .MuiToggleButton-root': {
    border: 'none',
    padding: '6px 16px',
    fontSize: '0.875rem',
    fontWeight: 500,
    textTransform: 'none',
    color: '#64748b',
    '&:hover': {
      backgroundColor: '#f8fafc',
    },
    '&.Mui-selected': {
      backgroundColor: '#3b82f6',
      color: '#ffffff',
      '&:hover': {
        backgroundColor: '#2563eb',
      },
    },
  },
});

const GanttPage: React.FC = () => {
  const navigate = useNavigate();
  const { project } = useProjectStore();
  const [viewMode, setViewMode] = useState<ViewMode>('role');
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});
  const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedItem, setSelectedItem] = useState<{ type: 'role' | 'person', value: string } | null>(null);
  const [hoveredBar, setHoveredBar] = useState<string | null>(null);
  
  // 新增日期範圍狀態
  const [dateRange, setDateRange] = useState<{
    startDate: Date | null;
    endDate: Date | null;
  }>({
    startDate: null,
    endDate: null,
  });
  
  const [filters, setFilters] = useState<FilterType>({
    tasks: [],
    subtasks: [],
    roles: [],
    assignees: [],
    taskPriorities: [],
    subtaskStatuses: [],
    showCompleted: true,
    showExpected: true,
  });

  // 初始化日期範圍為專案範圍
  React.useEffect(() => {
    if (project && !dateRange.startDate && !dateRange.endDate) {
      const startDate = new Date(project.startDate);
      const endDate = new Date(project.expectedDate);
      console.log('Initializing date range:', startDate, endDate);
      setDateRange({
        startDate: startDate,
        endDate: endDate,
      });
    }
  }, [project, dateRange.startDate, dateRange.endDate]);

  // 計算時間範圍
  const timeRange = useMemo(() => {
    if (!project) return { start: new Date(), end: new Date(), months: [] };
    
    const allDates = project.tasks.flatMap(task => [
      task.startDate,
      task.expectedDate,
      ...task.subtasks.map(st => st.startDate).filter(Boolean),
      ...task.subtasks.map(st => st.expectedDate).filter(Boolean),
    ]).filter(Boolean) as Date[];
    
    if (allDates.length === 0) return { start: new Date(), end: new Date(), months: [] };
    
    const start = new Date(Math.min(...allDates.map(d => new Date(d).getTime())));
    const end = new Date(Math.max(...allDates.map(d => new Date(d).getTime())));
    
    // 擴展到月份邊界
    start.setDate(1);
    end.setMonth(end.getMonth() + 1, 0);
    
    // 生成月份列表
    const months = [];
    const current = new Date(start);
    while (current <= end) {
      months.push(new Date(current));
      current.setMonth(current.getMonth() + 1);
    }
    
    return { start, end, months };
  }, [project]);

  // 獲取所有唯一值
  const uniqueValues = useMemo(() => {
    if (!project) return { tasks: [], subtasks: [], roles: [], assignees: [], priorities: [], statuses: [] };
    
    const tasks = project.tasks.map(t => t.title);
    const subtasks = project.tasks.flatMap(t => t.subtasks.map(st => st.text));
    const roles = Array.from(new Set(project.tasks.flatMap(t => t.roles)));
    const assignees = Array.from(new Set(project.tasks.flatMap(t => t.subtasks.flatMap(st => st.assignees))));
    const priorities: Priority[] = ['high', 'medium', 'low'];
    const statuses = ['pending', 'in-progress', 'pending-review', 'reviewing', 'completed', 'review-failed'];
    
    return { tasks, subtasks, roles, assignees, priorities, statuses };
  }, [project]);

  // 計算職能視圖數據
  const roleViewData = useMemo(() => {
    if (!project || viewMode !== 'role') return null;
    
    const roleData: Record<Role, {
      totalTasks: number;
      totalSubtasks: number;
      completedSubtasks: number;
      assignees: Record<string, {
        totalSubtasks: number;
        completedSubtasks: number;
      }>;
    }> = {} as any;
    
    // 初始化職能數據
    uniqueValues.roles.forEach(role => {
      roleData[role] = {
        totalTasks: 0,
        totalSubtasks: 0,
        completedSubtasks: 0,
        assignees: {},
      };
    });
    
    project.tasks.forEach(task => {
      // 應用過濾
      if (filters.tasks.length > 0 && !filters.tasks.includes(task.title)) return;
      if (filters.taskPriorities.length > 0 && !filters.taskPriorities.includes(task.priority)) return;
      
      task.roles.forEach(role => {
        if (filters.roles.length > 0 && !filters.roles.includes(role)) return;
        
        roleData[role].totalTasks++;
        
        task.subtasks.forEach(subtask => {
          if (subtask.role !== role) return;
          if (filters.subtasks.length > 0 && !filters.subtasks.includes(subtask.text)) return;
          if (filters.subtaskStatuses.length > 0 && !filters.subtaskStatuses.includes(subtask.status)) return;
          if (filters.assignees.length > 0 && !subtask.assignees.some(a => filters.assignees.includes(a))) return;
          
          roleData[role].totalSubtasks++;
          
          if (subtask.status === 'completed') {
            roleData[role].completedSubtasks++;
          }
          
          // 按人員統計
          subtask.assignees.forEach(assignee => {
            if (!roleData[role].assignees[assignee]) {
              roleData[role].assignees[assignee] = {
                totalSubtasks: 0,
                completedSubtasks: 0,
              };
            }
            
            roleData[role].assignees[assignee].totalSubtasks++;
            
            if (subtask.status === 'completed') {
              roleData[role].assignees[assignee].completedSubtasks++;
            }
          });
        });
      });
    });
    
    return roleData;
  }, [project, viewMode, filters, uniqueValues.roles]);

  // 計算人員視圖數據
  const personViewData = useMemo(() => {
    if (!project || viewMode !== 'person') return null;
    
    const personData: Record<string, {
      totalSubtasks: number;
      completedSubtasks: number;
      roles: Record<Role, {
        totalSubtasks: number;
        completedSubtasks: number;
      }>;
    }> = {};
    
    project.tasks.forEach(task => {
      // 應用過濾
      if (filters.tasks.length > 0 && !filters.tasks.includes(task.title)) return;
      if (filters.taskPriorities.length > 0 && !filters.taskPriorities.includes(task.priority)) return;
      
      task.subtasks.forEach(subtask => {
        if (subtask.assignees.length === 0) return;
        if (filters.subtasks.length > 0 && !filters.subtasks.includes(subtask.text)) return;
        if (filters.subtaskStatuses.length > 0 && !filters.subtaskStatuses.includes(subtask.status)) return;
        if (filters.assignees.length > 0 && !subtask.assignees.some(a => filters.assignees.includes(a))) return;
        if (filters.roles.length > 0 && !filters.roles.includes(subtask.role)) return;
        
        subtask.assignees.forEach(assignee => {
          if (filters.assignees.length > 0 && !filters.assignees.includes(assignee)) return;
          
          if (!personData[assignee]) {
            personData[assignee] = {
              totalSubtasks: 0,
              completedSubtasks: 0,
              roles: {} as any,
            };
          }
          
          personData[assignee].totalSubtasks++;
          
          if (subtask.status === 'completed') {
            personData[assignee].completedSubtasks++;
          }
          
          // 按職能統計
          if (!personData[assignee].roles[subtask.role]) {
            personData[assignee].roles[subtask.role] = {
              totalSubtasks: 0,
              completedSubtasks: 0,
            };
          }
          
          personData[assignee].roles[subtask.role].totalSubtasks++;
          
          if (subtask.status === 'completed') {
            personData[assignee].roles[subtask.role].completedSubtasks++;
          }
        });
      });
    });
    
    return personData;
  }, [project, viewMode, filters]);

  // 準備甘特圖數據
  const ganttData = useMemo(() => {
    if (!project) return { role: {}, person: {} };
    
    const roleData: Record<Role, GanttBar[]> = {} as any;
    const personData: Record<string, GanttBar[]> = {};
    
    // 初始化
    uniqueValues.roles.forEach(role => {
      roleData[role] = [];
    });
    
    project.tasks.forEach(task => {
      // 應用過濾
      if (filters.tasks.length > 0 && !filters.tasks.includes(task.title)) return;
      
      task.subtasks.forEach(subtask => {
        if (filters.roles.length > 0 && !filters.roles.includes(subtask.role)) return;
        if (filters.assignees.length > 0 && !subtask.assignees.some(a => filters.assignees.includes(a))) return;
        if (filters.subtaskStatuses.length > 0 && !filters.subtaskStatuses.includes(subtask.status)) return;
        
        const bar: GanttBar = {
          id: subtask.id,
          label: subtask.text,
          task: task.title,
          role: subtask.role,
          assignees: subtask.assignees,
          startDate: subtask.startDate ? new Date(subtask.startDate) : new Date(),
          endDate: subtask.expectedDate ? new Date(subtask.expectedDate) : new Date(),
          status: subtask.status,
          progress: subtask.status === 'completed' ? 100 : 
                   subtask.status === 'reviewing' ? 80 :
                   subtask.status === 'pending-review' ? 70 :
                   subtask.status === 'in-progress' ? 50 : 0,
        };
        
        // 添加到職能視圖
        if (!roleData[subtask.role]) roleData[subtask.role] = [];
        roleData[subtask.role].push(bar);
        
        // 添加到人員視圖
        subtask.assignees.forEach(assignee => {
          if (!personData[assignee]) personData[assignee] = [];
          personData[assignee].push(bar);
        });
      });
    });
    
    return { role: roleData, person: personData };
  }, [project, filters, uniqueValues.roles]);

  // 計算條形圖位置
  const calculateBarPosition = (startDate: Date, endDate: Date) => {
    const totalDays = differenceInDays(timeRange.end, timeRange.start);
    const startDays = differenceInDays(startDate, timeRange.start);
    const duration = differenceInDays(endDate, startDate);
    
    const left = (startDays / totalDays) * 100;
    const width = (duration / totalDays) * 100;
    
    return { left: `${left}%`, width: `${width}%` };
  };

  // 處理過濾器變更
  const handleFilterChange = (filterType: keyof FilterType, value: any) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value,
    }));
  };

  // 清除所有過濾器
  const clearAllFilters = () => {
    setFilters({
      tasks: [],
      subtasks: [],
      roles: [],
      assignees: [],
      taskPriorities: [],
      subtaskStatuses: [],
      showCompleted: true,
      showExpected: true,
    });
  };

  // 獲取當前視圖的數據
  const currentData = viewMode === 'role' ? ganttData.role : ganttData.person;
  const filteredData = Object.entries(currentData)
    .filter(([key]) => {
      if (searchTerm) {
        return key.toLowerCase().includes(searchTerm.toLowerCase());
      }
      return true;
    })
    .reduce((acc, [key, value]) => {
      if (value) {
        acc[key] = value as GanttBar[];
      }
      return acc;
    }, {} as Record<string, GanttBar[]>);

  // 統計數據
  const stats = useMemo(() => {
    const allBars = Object.values(filteredData).flat();
    const uniqueTasks = new Set(allBars.map(b => b.id));
    const completedBars = allBars.filter(b => b.status === 'completed');
    const inProgressBars = allBars.filter(b => b.status === 'in-progress' || b.status === 'pending-review' || b.status === 'reviewing');
    const delayedTasks = allBars.filter(b => 
      b.status !== 'completed' && new Date(b.endDate) < new Date()
    );
    const personnel = new Set(allBars.flatMap(b => b.assignees));
    
    return {
      totalTasks: uniqueTasks.size,
      completedTasks: completedBars.length,
      inProgressTasks: inProgressBars.length,
      activeTasks: uniqueTasks.size - completedBars.length,
      delayedTasks: delayedTasks.length,
      totalPersonnel: personnel.size,
    };
  }, [filteredData]);

  // 生成詳細統計數據（用於彈窗）
  const generateDetailStats = (type: 'role' | 'person', value: string) => {
    if (!project) return null;
    
    if (type === 'role') {
      const role = value as Role;
      const roleTasks = project.tasks.flatMap(task => 
        task.subtasks.filter(st => st.role === role)
      );
      
      const totalSubtasks = roleTasks.length;
      const completedSubtasks = roleTasks.filter(st => st.status === 'completed').length;
      
      // 按人員統計
      const assigneeStats: Record<string, { total: number, completed: number }> = {};
      roleTasks.forEach(subtask => {
        subtask.assignees.forEach(assignee => {
          if (!assigneeStats[assignee]) {
            assigneeStats[assignee] = { total: 0, completed: 0 };
          }
          assigneeStats[assignee].total++;
          if (subtask.status === 'completed') {
            assigneeStats[assignee].completed++;
          }
        });
      });
      
      return {
        type: 'role' as const,
        value: role,
        totalSubtasks,
        completedSubtasks,
        assigneeStats,
      };
    } else {
      const person = value;
      const personTasks = project.tasks.flatMap(task => 
        task.subtasks.filter(st => st.assignees.includes(person))
      );
      
      const totalSubtasks = personTasks.length;
      const completedSubtasks = personTasks.filter(st => st.status === 'completed').length;
      
      // 按職能統計
      const roleStats: Record<Role, { total: number, completed: number }> = {} as any;
      personTasks.forEach(subtask => {
        if (!roleStats[subtask.role]) {
          roleStats[subtask.role] = { total: 0, completed: 0 };
        }
        roleStats[subtask.role].total++;
        if (subtask.status === 'completed') {
          roleStats[subtask.role].completed++;
        }
      });
      
      return {
        type: 'person' as const,
        value: person,
        totalSubtasks,
        completedSubtasks,
        roleStats,
      };
    }
  };

  const detailStats = selectedItem ? generateDetailStats(selectedItem.type, selectedItem.value) : null;

  // 圖表選項
  const chartOptions: ChartOptions<'bar'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
      },
      title: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            return `${context.dataset.label}: ${context.parsed.y}%`;
          },
        },
      },
      datalabels: {
        anchor: 'end' as const,
        align: 'end' as const,
        formatter: (value) => `${value}%`,
        color: '#666',
        font: {
          size: 10,
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 100,
        ticks: {
          callback: function(value) {
            return value + '%';
          },
        },
      },
    },
  };

  // 計算基於工時的統計數據
  const { roleStats: timeBasedRoleStats, personStats: timeBasedPersonStats } = useMemo(() => {
    if (!project) return { roleStats: {} as Record<Role, any>, personStats: {} };
    
    console.log('Calculating time stats with date range:', dateRange);
    
    const roleStats: Record<Role, { 
      totalHours: number; 
      personnelHours: Record<string, number>; 
      tasks: Set<string>;
    }> = {} as any;
    
    const personStats: Record<string, { 
      totalHours: number; 
      roleHours: Record<Role, number>; 
      tasks: Set<string>;
    }> = {};

    let totalLogs = 0;
    let filteredLogs = 0;

    project.tasks.forEach((task) => {
      task.subtasks.forEach((subtask) => {
        const role = subtask.role;
        
        // 初始化角色統計
        if (!roleStats[role]) {
          roleStats[role] = { totalHours: 0, personnelHours: {}, tasks: new Set() };
        }
        
        // 處理子任務的工時記錄
        subtask.timeLogs?.forEach((log) => {
          totalLogs++;
          // 檢查日期範圍
          if (dateRange.startDate && dateRange.endDate) {
            const logDate = new Date(log.date);
            if (logDate < dateRange.startDate || logDate > dateRange.endDate) {
              console.log('Filtered out log:', log.date, 'not in range', dateRange.startDate, '-', dateRange.endDate);
              return;
            }
          }
          
          filteredLogs++;
          roleStats[role].totalHours += log.hours;
          roleStats[role].personnelHours[log.assignee] = 
            (roleStats[role].personnelHours[log.assignee] || 0) + log.hours;
          roleStats[role].tasks.add(task.id);
          
          // 人員統計
          if (!personStats[log.assignee]) {
            personStats[log.assignee] = { totalHours: 0, roleHours: {} as Record<Role, number>, tasks: new Set() };
          }
          personStats[log.assignee].totalHours += log.hours;
          personStats[log.assignee].roleHours[role] = 
            (personStats[log.assignee].roleHours[role] || 0) + log.hours;
          personStats[log.assignee].tasks.add(task.id);
        });
        
        // 處理細項的工時記錄
        subtask.subSubtasks.forEach((subSubtask) => {
          subSubtask.timeLogs?.forEach((log) => {
            totalLogs++;
            // 檢查日期範圍
            if (dateRange.startDate && dateRange.endDate) {
              const logDate = new Date(log.date);
              if (logDate < dateRange.startDate || logDate > dateRange.endDate) {
                console.log('Filtered out sub-subtask log:', log.date, 'not in range', dateRange.startDate, '-', dateRange.endDate);
                return;
              }
            }
            
            filteredLogs++;
            roleStats[role].totalHours += log.hours;
            roleStats[role].personnelHours[log.assignee] = 
              (roleStats[role].personnelHours[log.assignee] || 0) + log.hours;
            roleStats[role].tasks.add(task.id);
            
            // 人員統計
            if (!personStats[log.assignee]) {
              personStats[log.assignee] = { totalHours: 0, roleHours: {} as Record<Role, number>, tasks: new Set() };
            }
            personStats[log.assignee].totalHours += log.hours;
            personStats[log.assignee].roleHours[role] = 
              (personStats[log.assignee].roleHours[role] || 0) + log.hours;
            personStats[log.assignee].tasks.add(task.id);
          });
        });
      });
    });
    
    console.log('Time stats calculation complete. Total logs:', totalLogs, 'Filtered logs:', filteredLogs);
    console.log('Role stats:', roleStats);
    console.log('Person stats:', personStats);
    
    return { roleStats, personStats };
  }, [project, dateRange.startDate, dateRange.endDate]);

  if (!project) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
        <Header showGanttButton={false} />
        <Container>
          <Box sx={{ mt: 4 }}>
            <Typography variant="h5">請先選擇專案</Typography>
            <Button onClick={() => navigate('/')} sx={{ mt: 2 }}>
              返回首頁
            </Button>
          </Box>
        </Container>
      </Box>
    );
  }

  const filterMenuOpen = Boolean(filterAnchorEl);

  return (
    <PageContainer>
      <Header showGanttButton={false} />
      <ContentWrapper maxWidth="xl">
        {/* 頂部標題 */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" fontWeight="bold">
            專案甘特圖
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {project.name} - 時程視覺化管理
          </Typography>
        </Box>

        {/* 統計卡片 */}
        <StatsGrid>
          <StatCard>
            <StatIcon color={roleColors[project.tasks[0].roles[0]]}>
              <AssignmentInd />
            </StatIcon>
            <StatLabel>總任務數</StatLabel>
            <StatValue>{project.tasks.length}</StatValue>
          </StatCard>
          <StatCard>
            <StatIcon color={roleColors[project.tasks[0].roles[0]]}>
              <AssignmentInd />
            </StatIcon>
            <StatLabel>已完成任務</StatLabel>
            <StatValue>{stats.completedTasks}</StatValue>
          </StatCard>
          <StatCard>
            <StatIcon color={roleColors[project.tasks[0].roles[0]]}>
              <AssignmentInd />
            </StatIcon>
            <StatLabel>進行中任務</StatLabel>
            <StatValue>{stats.inProgressTasks}</StatValue>
          </StatCard>
          <StatCard>
            <StatIcon color={roleColors[project.tasks[0].roles[0]]}>
              <AssignmentInd />
            </StatIcon>
            <StatLabel>總工時（小時）</StatLabel>
            <StatValue>{Object.values(timeBasedRoleStats).reduce((sum, r) => sum + r.totalHours, 0).toFixed(1)}</StatValue>
          </StatCard>
        </StatsGrid>

        {/* 控制區 */}
        <ChartContainer elevation={0}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3, alignItems: 'center' }}>
            <Box sx={{ flex: 1, width: '100%' }}>
              <ViewToggleGroup
                value={viewMode}
                exclusive
                onChange={(e, newMode) => newMode && setViewMode(newMode)}
                fullWidth
              >
                <ToggleButton value="role">
                  <ViewModule sx={{ mr: 1 }} />
                  按職能分組
                </ToggleButton>
                <ToggleButton value="person">
                  <Person sx={{ mr: 1 }} />
                  按人員分組
                </ToggleButton>
              </ViewToggleGroup>
            </Box>
            
            <Box sx={{ flex: 1, width: '100%' }}>
              <TextField
                fullWidth
                size="small"
                placeholder="搜尋..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            
            <Box sx={{ flex: 1, width: '100%', display: 'flex', gap: 2, justifyContent: { xs: 'center', md: 'flex-end' } }}>
              <Button
                variant="outlined"
                startIcon={<FilterList />}
                onClick={(e) => setFilterAnchorEl(e.currentTarget)}
              >
                過濾器
                {Object.values(filters).some(f => Array.isArray(f) && f.length > 0) && (
                  <Chip 
                    size="small" 
                    label={Object.values(filters).filter(f => Array.isArray(f) && f.length > 0).length}
                    color="primary"
                    sx={{ ml: 1 }}
                  />
                )}
              </Button>
              
              {Object.values(filters).some(f => Array.isArray(f) && f.length > 0) && (
                <Button variant="text" onClick={clearAllFilters}>
                  清除過濾
                </Button>
              )}
            </Box>
          </Box>

          {/* 顯示過濾條件 */}
          {Object.values(filters).some(f => Array.isArray(f) && f.length > 0) && (
            <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {filters.tasks.map(task => (
                <Chip key={task} label={`任務: ${task}`} size="small" onDelete={() => handleFilterChange('tasks', filters.tasks.filter(t => t !== task))} />
              ))}
              {filters.roles.map(role => (
                <Chip key={role} label={`職能: ${role}`} size="small" onDelete={() => handleFilterChange('roles', filters.roles.filter(r => r !== role))} />
              ))}
              {filters.assignees.map(assignee => (
                <Chip key={assignee} label={`人員: ${assignee}`} size="small" onDelete={() => handleFilterChange('assignees', filters.assignees.filter(a => a !== assignee))} />
              ))}
              {filters.subtaskStatuses.map(status => (
                <Chip key={status} label={`狀態: ${status}`} size="small" onDelete={() => handleFilterChange('subtaskStatuses', filters.subtaskStatuses.filter(s => s !== status))} />
              ))}
            </Box>
          )}
        </ChartContainer>

        {/* 過濾器選單 */}
        <Menu
          anchorEl={filterAnchorEl}
          open={filterMenuOpen}
          onClose={() => setFilterAnchorEl(null)}
          PaperProps={{
            sx: { width: 400, maxHeight: 600 }
          }}
        >
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>過濾器</Typography>
            
            {/* 任務過濾 */}
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>任務</InputLabel>
              <Select
                multiple
                value={filters.tasks}
                onChange={(e) => handleFilterChange('tasks', e.target.value)}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip key={value} label={value} size="small" />
                    ))}
                  </Box>
                )}
              >
                {uniqueValues.tasks.map((task) => (
                  <MenuItem key={task} value={task}>
                    <Checkbox checked={filters.tasks.indexOf(task) > -1} />
                    <ListItemText primary={task} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* 優先級過濾 */}
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>任務優先級</InputLabel>
              <Select
                multiple
                value={filters.taskPriorities}
                onChange={(e) => handleFilterChange('taskPriorities', e.target.value)}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip 
                        key={value} 
                        label={value === 'high' ? '高' : value === 'medium' ? '中' : '低'} 
                        size="small"
                        sx={{ bgcolor: priorityColors[value] + '30', color: priorityColors[value] }}
                      />
                    ))}
                  </Box>
                )}
              >
                {uniqueValues.priorities.map((priority) => (
                  <MenuItem key={priority} value={priority}>
                    <Checkbox checked={filters.taskPriorities.indexOf(priority) > -1} />
                    <ListItemIcon>
                      <Flag sx={{ color: priorityColors[priority] }} />
                    </ListItemIcon>
                    <ListItemText primary={priority === 'high' ? '高優先' : priority === 'medium' ? '中優先' : '低優先'} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* 職能過濾 */}
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>職能</InputLabel>
              <Select
                multiple
                value={filters.roles}
                onChange={(e) => handleFilterChange('roles', e.target.value)}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip 
                        key={value} 
                        label={value} 
                        size="small"
                        sx={{ bgcolor: roleColors[value] + '30', color: roleColors[value] }}
                      />
                    ))}
                  </Box>
                )}
              >
                {uniqueValues.roles.map((role) => (
                  <MenuItem key={role} value={role}>
                    <Checkbox checked={filters.roles.indexOf(role) > -1} />
                    <ListItemText primary={role} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* 負責人過濾 */}
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>負責人</InputLabel>
              <Select
                multiple
                value={filters.assignees}
                onChange={(e) => handleFilterChange('assignees', e.target.value)}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip key={value} label={value} size="small" />
                    ))}
                  </Box>
                )}
              >
                {uniqueValues.assignees.map((assignee) => (
                  <MenuItem key={assignee} value={assignee}>
                    <Checkbox checked={filters.assignees.indexOf(assignee) > -1} />
                    <ListItemText primary={assignee} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* 子任務狀態過濾 */}
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>子任務狀態</InputLabel>
              <Select
                multiple
                value={filters.subtaskStatuses}
                onChange={(e) => handleFilterChange('subtaskStatuses', e.target.value)}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip key={value} label={value} size="small" />
                    ))}
                  </Box>
                )}
              >
                {uniqueValues.statuses.map((status) => (
                  <MenuItem key={status} value={status}>
                    <Checkbox checked={filters.subtaskStatuses.indexOf(status) > -1} />
                    <ListItemText primary={status} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Menu>

        {/* 甘特圖 */}
        <Paper sx={{ p: 3, overflow: 'auto' }}>
          <Box sx={{ minWidth: 1200 }}>
            {/* 時間軸標題 */}
            <Box sx={{ display: 'flex', borderBottom: 2, borderColor: 'divider', mb: 2, position: 'sticky', top: 0, bgcolor: 'background.paper', zIndex: 10 }}>
              <Box sx={{ width: 200, p: 2, borderRight: 2, borderColor: 'divider' }}>
                <Typography variant="h6">{viewMode === 'role' ? '職能' : '人員'}</Typography>
              </Box>
              <Box sx={{ flex: 1, display: 'flex' }}>
                {timeRange.months.map((month, index) => (
                  <Box
                    key={index}
                    sx={{
                      flex: 1,
                      p: 1,
                      textAlign: 'center',
                      borderRight: 1,
                      borderColor: 'divider',
                    }}
                  >
                    <Typography variant="body2" fontWeight="bold">
                      {format(month, 'yyyy年M月')}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>

            {/* 甘特圖行 */}
            {Object.entries(filteredData).map(([key, bars]) => (
              <Box
                key={key}
                sx={{
                  display: 'flex',
                  borderBottom: 1,
                  borderColor: 'divider',
                  minHeight: 50,
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                {/* 標籤 */}
                <Box
                  sx={{
                    width: 200,
                    p: 2,
                    borderRight: 2,
                    borderColor: 'divider',
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'action.selected' },
                  }}
                  onClick={() => setSelectedItem({ type: viewMode, value: key })}
                >
                  {viewMode === 'person' ? (
                    <>
                      <Avatar sx={{ width: 28, height: 28, mr: 1, bgcolor: 'primary.main', fontSize: '0.875rem' }}>
                        {key.charAt(0)}
                      </Avatar>
                      <Typography variant="body2" fontWeight="medium">{key}</Typography>
                    </>
                  ) : (
                    <Chip
                      label={key}
                      size="small"
                      sx={{
                        bgcolor: roleColors[key as Role] + '30',
                        color: roleColors[key as Role],
                        fontWeight: 'bold',
                      }}
                    />
                  )}
                </Box>

                {/* 甘特條 */}
                <Box sx={{ flex: 1, position: 'relative', minHeight: 50 }}>
                  {/* 月份網格線 */}
                  {timeRange.months.map((_, index) => (
                    <Box
                      key={index}
                      sx={{
                        position: 'absolute',
                        left: `${(index / timeRange.months.length) * 100}%`,
                        top: 0,
                        bottom: 0,
                        width: 1,
                        bgcolor: 'divider',
                      }}
                    />
                  ))}

                  {/* 任務條 */}
                  {bars.map((bar) => {
                    const position = calculateBarPosition(bar.startDate, bar.endDate);
                    return (
                      <Tooltip
                        key={bar.id}
                        title={
                          <Box>
                            <Typography variant="body2" fontWeight="bold">{bar.label}</Typography>
                            <Typography variant="caption">任務: {bar.task}</Typography><br />
                            <Typography variant="caption">負責人: {bar.assignees.join(', ')}</Typography><br />
                            <Typography variant="caption">期間: {format(bar.startDate, 'MM/dd')} ~ {format(bar.endDate, 'MM/dd')}</Typography><br />
                            <Typography variant="caption">狀態: {bar.status}</Typography>
                          </Box>
                        }
                      >
                        <Box
                          sx={{
                            position: 'absolute',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            left: position.left,
                            width: position.width,
                            height: 30,
                            background: taskColors[bar.task] || 'linear-gradient(135deg, #667eea, #764ba2)',
                            borderRadius: 15,
                            display: 'flex',
                            alignItems: 'center',
                            px: 1.5,
                            cursor: 'pointer',
                            overflow: 'hidden',
                            opacity: bar.status === 'completed' ? 0.8 : 1,
                            border: bar.status === 'review-failed' ? '2px solid #e53e3e' : 'none',
                            transition: 'all 0.2s',
                            '&:hover': {
                              transform: 'translateY(-50%) translateY(-2px)',
                              boxShadow: 3,
                              zIndex: 2,
                            },
                          }}
                          onMouseEnter={() => setHoveredBar(bar.id)}
                          onMouseLeave={() => setHoveredBar(null)}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              color: 'white',
                              fontWeight: 'medium',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {bar.label}
                          </Typography>
                        </Box>
                      </Tooltip>
                    );
                  })}
                </Box>
              </Box>
            ))}
          </Box>
        </Paper>

        {/* 圖例 */}
        <Box sx={{ mt: 2, display: 'flex', gap: 3, flexWrap: 'wrap', justifyContent: 'center' }}>
          {Object.entries(taskColors).map(([task, color]) => (
            <Box key={task} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 20, height: 12, background: color, borderRadius: 1 }} />
              <Typography variant="caption">{task}</Typography>
            </Box>
          ))}
        </Box>

        {/* 詳細統計彈窗 */}
        <Dialog
          open={!!selectedItem}
          onClose={() => setSelectedItem(null)}
          maxWidth="md"
          fullWidth
        >
          {selectedItem && detailStats && (
            <>
              <DialogTitle>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {detailStats.type === 'role' ? (
                      <Chip
                        label={detailStats.value}
                        sx={{
                          bgcolor: roleColors[detailStats.value as Role] + '30',
                          color: roleColors[detailStats.value as Role],
                          fontWeight: 'bold',
                        }}
                      />
                    ) : (
                      <>
                        <Avatar sx={{ bgcolor: 'primary.main' }}>
                          {detailStats.value.charAt(0)}
                        </Avatar>
                        <Typography variant="h6">{detailStats.value}</Typography>
                      </>
                    )}
                  </Box>
                  <IconButton onClick={() => setSelectedItem(null)}>
                    <Close />
                  </IconButton>
                </Box>
              </DialogTitle>
              <DialogContent>
                {/* 總體統計 */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" gutterBottom>總體統計</Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2 }}>
                    <Card>
                      <CardContent>
                        <Typography variant="h4">{detailStats.totalSubtasks}</Typography>
                        <Typography variant="body2" color="text.secondary">總任務數</Typography>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent>
                        <Typography variant="h4" color="success.main">
                          {Math.round((detailStats.completedSubtasks / detailStats.totalSubtasks) * 100)}%
                        </Typography>
                        <Typography variant="body2" color="text.secondary">已完成</Typography>
                      </CardContent>
                    </Card>
                  </Box>
                </Box>

                {/* 進度條 */}
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">總體進度</Typography>
                    <Typography variant="body2">
                      {Math.round((detailStats.completedSubtasks / detailStats.totalSubtasks) * 100)}%
                    </Typography>
                  </Box>
                  <Box sx={{ position: 'relative' }}>
                    <LinearProgress
                      variant="determinate"
                      value={Math.round((detailStats.completedSubtasks / detailStats.totalSubtasks) * 100)}
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        bgcolor: 'grey.200',
                        '& .MuiLinearProgress-bar': {
                          bgcolor: 'success.main',
                        },
                      }}
                    />
                  </Box>
                </Box>

                <Divider sx={{ my: 3 }} />

                {/* 分布圖表 */}
                <Typography variant="h6" gutterBottom>
                  {detailStats.type === 'role' ? '人員分布' : '職能分布'}
                </Typography>
                <Box sx={{ height: 300 }}>
                  {detailStats.type === 'role' ? (
                    <Bar
                      data={{
                        labels: Object.keys(detailStats.assigneeStats),
                        datasets: [
                          {
                            label: '已完成',
                            data: Object.values(detailStats.assigneeStats).map(s => 
                              s.total > 0 ? Math.round((s.completed / s.total) * 100) : 0
                            ),
                            backgroundColor: roleColors[detailStats.value as Role],
                            borderColor: roleColors[detailStats.value as Role],
                            borderWidth: 1,
                          },
                        ],
                      }}
                      options={chartOptions}
                    />
                  ) : (
                    <Bar
                      data={{
                        labels: Object.keys(detailStats.roleStats),
                        datasets: [
                          {
                            label: '已完成',
                            data: Object.entries(detailStats.roleStats).map(([role, s]) => 
                              s.total > 0 ? Math.round((s.completed / s.total) * 100) : 0
                            ),
                            backgroundColor: Object.keys(detailStats.roleStats).map(role => roleColors[role as Role]),
                            borderColor: Object.keys(detailStats.roleStats).map(role => roleColors[role as Role]),
                            borderWidth: 1,
                          },
                        ],
                      }}
                      options={chartOptions}
                    />
                  )}
                </Box>
              </DialogContent>
            </>
          )}
        </Dialog>

        {/* 工時分佈視圖 */}
        <TimeDistributionSection elevation={0}>
          <ChartHeader>
            <ChartTitle>工時分佈</ChartTitle>
            
            {/* 日期範圍選擇器 */}
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={zhTW}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <DateRange sx={{ mr: 1 }} />
                <DatePicker
                  label="開始日期"
                  value={dateRange.startDate}
                  onChange={(newValue) => setDateRange(prev => ({ ...prev, startDate: newValue }))}
                  format="yyyy-MM-dd"
                  slotProps={{
                    textField: { size: 'small', sx: { width: 180 } }
                  }}
                />
                <Typography>至</Typography>
                <DatePicker
                  label="結束日期"
                  value={dateRange.endDate}
                  onChange={(newValue) => setDateRange(prev => ({ ...prev, endDate: newValue }))}
                  format="yyyy-MM-dd"
                  slotProps={{
                    textField: { size: 'small', sx: { width: 180 } }
                  }}
                />
                <Button
                  size="small"
                  onClick={() => setDateRange({ startDate: project?.startDate || null, endDate: project?.expectedDate || null })}
                >
                  重設
                </Button>
              </Box>
            </LocalizationProvider>
          </ChartHeader>
          
          <TimeDistributionGrid>
            {Object.entries(viewMode === 'role' ? timeBasedRoleStats : timeBasedPersonStats).map(([key, data]) => {
              const isRole = viewMode === 'role';
              const typedData = data as { totalHours: number; personnelHours: Record<string, number>; roleHours: Record<Role, number>; tasks: Set<string> };
              const subData = isRole ? typedData.personnelHours : typedData.roleHours;
              
              return (
                <TimeCard key={key}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {isRole ? (
                        <Chip
                          label={key}
                          size="small"
                          sx={{
                            bgcolor: roleColors[key as Role] + '30',
                            color: roleColors[key as Role],
                            fontWeight: 'bold',
                          }}
                        />
                      ) : (
                        <>
                          <PersonAvatar>
                            {key.charAt(0)}
                          </PersonAvatar>
                          <Typography variant="subtitle1" fontWeight="bold">
                            {key}
                          </Typography>
                        </>
                      )}
                    </Box>
                    <Chip 
                      label={`${typedData.totalHours.toFixed(1)} 小時`}
                      size="small" 
                      color="primary"
                    />
                  </Box>
                  
                  {Object.entries(subData).length > 0 ? (
                    <Box>
                      {Object.entries(subData)
                        .sort(([, a], [, b]) => (b as number) - (a as number))
                        .map(([subKey, hours]) => {
                          const percentage = typedData.totalHours > 0 ? ((hours as number / typedData.totalHours) * 100).toFixed(1) : '0';
                          
                          return (
                            <Box key={subKey} sx={{ mb: 1 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                <Typography variant="body2">{subKey}</Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {(hours as number).toFixed(1)}h ({percentage}%)
                                </Typography>
                              </Box>
                              <ProgressBar
                                variant="determinate"
                                value={Number(percentage)}
                                sx={{
                                  height: 6,
                                  borderRadius: 3,
                                  backgroundColor: 'action.hover',
                                  '& .MuiLinearProgress-bar': {
                                    backgroundColor: isRole ? 
                                      roleColors[key as Role] : 
                                      roleColors[subKey as Role] || '#999',
                                  },
                                }}
                              />
                            </Box>
                          );
                        })}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      尚無工時記錄
                    </Typography>
                  )}
                  
                  {/* 顯示總計百分比說明 */}
                  {isRole && Object.entries(subData).length > 0 && (
                    <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                      <Typography variant="caption" color="text.secondary">
                        此職能總工時分配 (各人員工時總和 = 100%)
                      </Typography>
                    </Box>
                  )}
                </TimeCard>
              );
            })}
          </TimeDistributionGrid>
        </TimeDistributionSection>
      </ContentWrapper>
    </PageContainer>
  );
};

export default GanttPage; 